import Head from "next/head"
import styles from "@/styles/checkout.module.scss"
import CheckoutLayout from "@/components/CheckoutLayout"
import Steps from "@/components/checkout/steps"
import { Image, Affix, Button, Radio, Col, Row, Skeleton, message, Popconfirm, Modal, ConfigProvider } from "antd"

import getConfig from 'next/config'
import { GoogleOAuthProvider } from '@react-oauth/google';
import AuthenticatedCheckout from "@/components/checkout/AuthenticatedCheckout"
import { CloseOutlined, LeftOutlined, UpOutlined, DownOutlined, WarningOutlined } from "@ant-design/icons"
import { useEffect, useRef, useState } from "react"
import GuestCheckout from "@/components/checkout/GuestCheckout"
const { publicRuntimeConfig } = getConfig()
const imageDomain = publicRuntimeConfig.imageDomain
import { updateUserInfo } from '@/store/slices/userInfoSlice';
import { useDispatch } from "react-redux"
import { AppDispatch } from '@/store/store';
import myAccountApi from "@/service/modules/myAccountApi"
import router, { useRouter } from "next/router"
import { updateCarList } from "@/store/slices/cartListSlice"
import orderApi from "@/service/modules/orderApi"
import viewcartApi from "@/service/modules/viewcartApi"
import { gaBeginCheckout } from "@/utils/gaEvent"
import paymentApi from "@/service/modules/paymentApi"
import { criteoBeginCheckout } from "@/utils/criteoEvent"
import { formatCurrency, getCompressedImageUrl } from "@/utils/utils"
import LearnMore from "@/components/checkout/LearnMore"
import { matomoSetCustomDimension } from '@/utils/matomoEvent';
import { isNativeApplePayAvailable } from "@/utils/applePayAvailability"
import React from "react"

type CheckoutParams = {
  isAppleCanNative: number;
  productID?: string | string[];
  quantity?: string | string[] | number;
};

const getIsAppleCanNative = () => {
  if (typeof window === "undefined") {
    return 0;
  }

  return isNativeApplePayAvailable(window, window.location.protocol) ? 1 : 0;
};

export default function Checkout() {
  const router = useRouter()
  const dispatch: AppDispatch = useDispatch();
  const appleCanNativeOnEnterRef = useRef(getIsAppleCanNative());

  const [paymentType, setPaymentType] = useState("")
  const [userInfo, setUserInfo] = useState<any>({})
  const [carList, setCarList] = useState<any[]>([])

  const [paymentList, setPaymentList] = useState<any[]>([]);
  const [cartInfo, setCartInfo] = useState<any>({})
  const [showOrderDetail, setShowOrderDetail] = useState(true)
  const [checkoutLoading, setCheckoutLoading] = useState(true)
  const [checkoutBtnLoading, setCheckoutBtnLoading] = useState(false)
  const [isPre, setIsPre] = useState(false)
  const [windowWidth, setWindowWidth] = useState(0);
  const [canMakeApplePay, setCanMakeApplePay] = useState(false);

  // 等待页面onload 后，再执行苹果支付校验的代码
  useEffect(() => {
    const handleLoad = () => {
      // 不是https协议，苹果reject
      if (document.location.protocol != 'https:') {
        setCanMakeApplePay(false);
        return;
      }

      const js = document.createElement('script');
      js.id = 'apple-pay-js';
      js.src = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
      js.crossOrigin = 'anonymous';
      js.async = true;

      js.onload = () => {
        const ApplePaySession = (window as any).ApplePaySession;

        if (window && ApplePaySession && ApplePaySession.canMakePayments()) {
          setCanMakeApplePay(true);
        } else {
          setCanMakeApplePay(false);
        }
      };
      js.onerror = () => {
        setCanMakeApplePay(false);
      };
      document.body.appendChild(js);
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }

    return () => {
      window.removeEventListener('load', handleLoad);
      const existingScript = document.getElementById('apple-pay-js');
      existingScript?.remove();
    };
  }, []);

  useEffect(() => {
    // 设置初始宽度
    setWindowWidth(window.innerWidth);

    // 定义 resize 事件处理器
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // 监听窗口大小变化
    window.addEventListener('resize', handleResize);

    // 清理事件监听器
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 切换支付方式
  const paymentChange = (e: any) => {
    const { value } = e.target
    setPaymentType(value);
  }

  useEffect(() => {
    // 获取购物车列表
    if (router.isReady) {
      //获取用户信息
      getUserInfo()
      if (router.query && router.query.p) {
        setIsPre(true)
        checkout()
        checkOrder()
      } else {
        getCarList()
      }
    }
  }, [router.isReady])

  // 在页面加载时触发的逻辑,通过监听 pageshow 事件并检查 event.persisted，可以判断页面是否是从 BFCache 恢复的。如果是，你可以强制触发 useEffect。
  useEffect(() => {
    // 在页面加载时触发的逻辑
    const handlePageShow = (event: any) => {
      if (event.persisted) {
        // 处理页面从缓存中恢复时的逻辑
        window.location.reload(); // 或者你可以选择调用某个函数来手动触发需要的逻辑
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const getCarList = async () => {
    try {
      const shopCartData = await viewcartApi.getList();
      const { list: { available }, selfUpdated } = shopCartData.data
      if (!available) {
        // 购物车没有东西, 跳到指定页面
        checkOrder()
        return
      }
      if (selfUpdated) {
        message.warning("Your cart has been updated due to limited stock. Please review your order before checkout.")
      }
      const filterList = available.filter((v: any) => v.appSelectStatus == 1)
      setCarList(filterList)
      // 更新状态购物车数据
      dispatch(updateCarList(shopCartData));
      if (filterList.length) {
        checkout()
      } else {
        // 购物车没有选中的商品, 跳到指定页面
        checkOrder()
      }
    } catch (error: any) {
      console.log(error.message, ' === error.message getCarList');
      message.error(error.message)
    }
  }

  // 确认是否有未支付的订单, 有的话跳转到订单详情页面
  const checkOrder = async () => {
    try {
      let dataObj = {}
      if (router.query && router.query.p) {
        dataObj = {
          productID: router.query.p,
          quantity: router.query.t || 1
        }
      }
      const res = await orderApi.checkOrder(dataObj);

      if (res.data && res.data.orderID) {
        router.replace(`/my-account/order-details/${res.data.orderID}`);
      } else {
        if (!router.query.p) {
          router.replace(`/view-cart`);
        }
      }
    } catch (error: any) {
      console.log(error.message, ' === error.message checkOrder');
    }
  }

  // 获取用户登录信息
  const getUserInfo = async () => {
    try {
      const res = await myAccountApi.getCurrentCustomerInfo()
      dispatch(updateUserInfo(res.data))
      setUserInfo(res.data)
    } catch (error) {
      console.log(error)
    }
  }

  // 获取checkout信息
  const checkout = async () => {
    try {
      setCheckoutLoading(true)
      let params: CheckoutParams = {
        isAppleCanNative: appleCanNativeOnEnterRef.current,
      }
      if (router.query && router.query.p) {
        params = {
          ...params,
          productID: router.query.p,
          quantity: router.query.t || 1
        }
      }
      let res = await orderApi.checkout(params)
      const { cart, paymentList } = res.data
      setPaymentList(paymentList)
      setCartInfo(cart)
      setCheckoutLoading(false)
      setCarList(cart.items)
      if (cart.selfUpdated) {
        message.warning("Your cart has been updated due to limited stock. Please review your order before checkout.")
      }
      const items = []
      const criteoItems = []
      for (const key in cart.items) {
        if (Object.prototype.hasOwnProperty.call(cart.items, key)) {
          const element = cart.items[key];
          items.push({
            item_id: element.productID,
            item_name: element.productName,
            price: element.price,
            quantity: element.quantity
          })
          criteoItems.push({
            id: element.productID,
            name: element.productName,
            price: element.price,
            quantity: element.quantity
          })
        }
      }

      // 发送ga事件
      gaBeginCheckout({
        currency: "AUD",
        value: cart.subTotal,
        items: items,
      })

      // 发送criteo事件
      criteoBeginCheckout(criteoItems)
    } catch (error: any) {
      message.error(error.message)
    }
  }

  // 对比购物车数据, 确保数据一致
  const compareCart = async () => {
    // 先compare, 确保数据一致,只需要传递选中的数据
    const compareList = carList.map((v: any) => ({
      "pid": v.productID,
      "qty": v.quantity,
      "upgrade": v.upgradeExpress,
      "selected": v.appSelectStatus,
      "from": v.fromZone
    }));
    const diffData = await viewcartApi.compareCart({ items: compareList })
    const { data: { isSame } } = diffData
    return isSame
  }

  // 创建订单
  const createOrder = async () => {
    try {
      if (!(userInfo && userInfo.customerID)) {
        message.warning("Please confirm the shipping address first !")
        return
      }
      setCheckoutBtnLoading(true)
      if (!(router.query && router.query.p)) {
        // 预售不需要比较数据
        const isSame = await compareCart()
        if (!isSame) {
          // 数据变化，重新拉取checkout数据
          checkout()
          throw new Error("Your shopping cart has been updated due to changes made in another tab. The page will refresh to reflect the latest data.")
        }
      }

      let createParms: any = { paymentID: paymentType }
      if (router.query && router.query.p) {
        createParms = {
          ...createParms,
          productID: router.query.p,
          quantity: router.query.t || 1
        }
      }

      const res = await orderApi.create(createParms)
      setCheckoutBtnLoading(false)

      // pushScarabQueueEvent(cartInfo, res.data.orderID)

      if (res.data.orderID) {
        matomoSetCustomDimension(30, res.data.orderID)
      }

      // 跳转到支付页面
      if (res.data && res.data.paymentID) {
        let payUrl = ''
        switch (String(res.data.paymentID)) {
          case '13':
            // Braintree
            verifyPaymentError(res.data.orderID, String(res.data.paymentID))
            break
          case '19':
            // adyen
            verifyPaymentError(res.data.orderID, String(res.data.paymentID))
            break
          case '6':
            // b-pay
            payUrl = `/checkout/b-pay/${res.data.orderID}`
            break
          case '5':
            // paypal
            payUrl = `/checkout/paypal/${res.data.orderID}`
            break
          case '12':
            // Braintree Pay Later
            payUrl = `/checkout/braintree-pay-later/${res.data.orderID}`
            break
          case '14':
            // Braintree Apple Pay
            payUrl = `/checkout/braintree-apple-pay/${res.data.orderID}`
            break
          case '16':
            // Braintree Google Pay
            payUrl = `/checkout/braintree-google-pay/${res.data.orderID}`
            break
          case '17':
            // Afterpay
            payUrl = `/checkout/after-pay/${res.data.orderID}`
            break
          case '29':
            // Zippay
            payUrl = `/checkout/zip-pay/${res.data.orderID}`
            break
          case '20':
            // Apple pay
            payUrl = `/checkout/adyen-apple-pay/${res.data.orderID}`
            break
          case '22':
            // Google pay
            payUrl = `/checkout/adyen-google-pay/${res.data.orderID}`
            break
          default:
            console.log('no match payment');
        }

        if (payUrl) {
          setTimeout(() => {
            router.push(payUrl)
          }, 500);
        }
      }
    } catch (error: any) {
      message.error(error.message)
      setCheckoutBtnLoading(false)
    }
  }

  // 针对braintree和adyen的支付，需要先验证错误次数再跳转
  const verifyPaymentError = async (orderID: number, paymentID: string) => {
    try {
      setCheckoutBtnLoading(true)
      const res = await paymentApi.creditCardErrorCheck()
      setCheckoutBtnLoading(false)
      if (res && res.data) {
        let payUrl = ''
        switch (paymentID) {
          case '13':
            // Braintree
            payUrl = `/checkout/braintree/${orderID}`
            break
          case '19':
            // adyen
            payUrl = `/checkout/adyen/${orderID}`
            break
          default:
            console.log('no match payment');
        }
        if (payUrl) router.push(payUrl)
      }
    } catch (error: any) {
      message.error(error.message)
      setCheckoutBtnLoading(false)
    }
  }

  // 删除购物车商品
  const deleteCartItems = async (item: any, resolve: Function) => {
    try {
      await viewcartApi.deleteCart({ pids: [item.productID] })
      // 重新拉取checkout数据
      await checkout()

      resolve(null)
    } catch (error: any) {
      message.error(error.message)
    }
  }

  // 点击步进器，跳转到对应页面
  const handleStepClick = (step: number) => {
    if (step === 0) {
      router.push('/view-cart')
    }
  };

  // 注入Emarsys代码
  const RECOMMENDER_SUBDOMAIN = 'https://recommender.scarabresearch.com/js/1CBC47CFE1C81625/scarab-v2.js';
  const CDN_SUBDOMAIN = 'http://cdn.scarabresearch.com/js/1CBC47CFE1C81625/scarab-v2.js';

  // 推送到scarabQueue事件
  function pushScarabQueueEvent(orderInfo: any, orderId: string) {
    if (window && (window as any).ScarabQueue) {
      if (localStorage.getItem('userEmail')) {
        (window as any).ScarabQueue.push(['setEmail', localStorage.getItem('userEmail')]);
      }

      var emarsysItems = Object.entries(orderInfo.items).map(function ([key, item]: any) {
        return {
          item: item.productID,
          quantity: item.quantity,
          price: item.price
        }
      });

      var scarabQueuePurchaseData = {
        orderId,
        items: emarsysItems
      };

      (window as any).ScarabQueue.push(['purchase', scarabQueuePurchaseData]);
      (window as any).ScarabQueue.push(['go']);
    } else {
      console.log('no window.ScarabQueue');
    }
  }

  // 注入
  // useEffect(() => {
  //   const subdomain = document.location.protocol === 'https:' ? RECOMMENDER_SUBDOMAIN : CDN_SUBDOMAIN;
  //   const id = 'scarab-js-api';

  //   if (document.getElementById(id)) return;

  //   const js = document.createElement('script');
  //   js.id = id;
  //   js.src = subdomain;

  //   const fs = document.getElementsByTagName('script')[0];
  //   fs?.parentNode?.insertBefore(js, fs);

  //   js.onload = () => {
  //     console.log('scarab js loaded');
  //   }

  //   return () => {
  //     js.remove();
  //   };
  // }, []);

  const [learnMoreItem, setLearnMoreItem] = useState<any>({});
  const [showLearnMore, setShowLearnMore] = useState<boolean>(false);

  const learnMore = (item: any) => {
    setLearnMoreItem(item)
    setShowLearnMore(true);
  }

  const [clientId, setClientId] = useState('');

  useEffect(() => {
    const hostname = window.location.hostname;
    let clientId;

    if (hostname === 'www.crazysales.com.au') {
      clientId = '643230432397-9qc19dbq4uoa4tikeithrnaj85r6gmvm.apps.googleusercontent.com';
    } else {
      clientId = '643230432397-lsjfp81vnovet7l9nahi6a3u8274mnpu.apps.googleusercontent.com';
    }

    setClientId(clientId);
  }, []);

  if (!clientId) return null; // Wait for clientId to be set

  return (
    <>
      <Head>
        <title>Online Shopping Australia – Beauty &amp; Health, Computers, Toys, - Deals &amp; Discounts | CrazySales</title>
        <meta name="keywords" content="Online Shopping Australia, online shopping sites, Home Shopping, Shopping Deals, Best Deals, Home Garden, Pet Supplies, Sports Fitness, Bedding Bath, Buy Online, Discount Shopping, Toys Hobbies, Beauty Health, Crazy Sales, Computers, IT, Christmas, Electronics, Automotive, Tools, Appliances, Baby, CrazySales" />
        <meta name="description" content="CrazySales.com.au - Australia&#039;s #1 online shopping website! Cheap bargains &amp; discounts with the top online shopping Australia store! From furniture, to toys &amp; hobbies, to appliances &amp; pet supplies -- we&#039;ve got it all, with free shipping on selected items!" />
        <meta name="geo.placename" content="Australia" />
        <meta name="geo.position" content="-25.274398;133.775136" />
        <meta name="ICBM" content="-25.274398, 133.775136" />
        <meta name="viewport" content="width=1100, initial-scale=0.75" />
      </Head>
      <GoogleOAuthProvider clientId={clientId}>
        <ConfigProvider theme={{
          token: {},
          components: {
            Button: {
              colorBgContainerDisabled: 'rgba(215, 11, 23, 0.4)',
              colorTextDisabled: '#fff'
            },
            Input: {
              colorBgContainerDisabled: '#f5f5f5',
            }
          }
        }}>
          <CheckoutLayout>
            <div className={styles['main']}>
              <div className={styles['checkout']}>

                {/* 步进器 */}
                <Steps value={1} onStepClick={handleStepClick} />

                <div className={styles['banner-img']}>
                  <Image src={`${imageDomain}/checkout/100secure.png`} width="100%" preview={false} />
                </div>

                <div className={styles['payment-container']}>
                  <div className={styles['left-side']}>
                    <div className={styles['payment-title']}>
                      Please input your Shipping information & choose your Payment Option
                    </div>

                    {
                      (userInfo && userInfo.customerID)
                        ?
                        <GuestCheckout {...userInfo} checkout={checkout} />
                        :
                        <AuthenticatedCheckout changeLoginStatus={() => { getUserInfo(); checkout() }} />
                    }
                    <div className={styles['checkout-step-item']}>
                      <div className={styles['stepTitle']}>3. Your Payment Options</div>

                      <p style={{ color: '#333', fontSize: '14px' }}>Choose your preferred payment option below:</p>
                      <p style={{ color: '#999', fontSize: '14px', margin: '8px 0 12px' }}>(Afterpay only support up to $4000 check-out value in each personal account)</p>

                      {
                        checkoutLoading
                          ?
                          <Skeleton active />
                          :
                          <>
                            <Radio.Group onChange={paymentChange} value={paymentType}>
                              <Row justify="space-between" gutter={24} >
                                {
                                  paymentList.map((item: any) => (
                                    <Col
                                      span={12}
                                      key={item.pid}
                                      style={
                                        item.pid == 20 && !canMakeApplePay
                                          ? { display: 'none' }
                                          : { marginBottom: '24px' }
                                      }
                                    >
                                      <Radio value={item.pid}>
                                        <div className={styles['payment-item']}>
                                          <Image src={item.icon} preview={false} style={{ width: '154px', flexShrink: '0' }} />
                                          <div className={styles['payment-info']}>
                                            <div className={styles['payment-info-title']}>{item.shortDescription}</div>
                                            <Button type="link" className={styles['payment-info-learn']} onClick={() => { learnMore(item) }}>Learn More</Button>
                                          </div>
                                        </div>
                                      </Radio>
                                    </Col>
                                  ))
                                }
                              </Row>
                            </Radio.Group>

                            <div className={styles['proceed-box']}>
                              <Button className={styles['proceed-btn']} type="primary" disabled={!paymentType || cartInfo.shippingError == 1} loading={checkoutBtnLoading} onClick={createOrder}>Proceed With Payment Option</Button>
                              <div className={styles['proceed-txt']}>Your <Button type="link" href="/article/PrivacyStatement" className={styles['privacy']}>Privacy & Security</Button> are assured</div>
                            </div>
                          </>
                      }
                    </div>
                  </div>
                  {
                    windowWidth > 1200
                      ?
                      <Affix offsetTop={8} className={styles['affix']}>
                        <LeftContent
                          cartInfo={cartInfo}
                          checkoutLoading={checkoutLoading}
                          isPre={isPre}
                          setShowOrderDetail={setShowOrderDetail}
                          showOrderDetail={showOrderDetail}
                          deleteCartItems={deleteCartItems}
                        />
                      </Affix>
                      :
                      <div className={styles['affix']}>
                        <LeftContent
                          cartInfo={cartInfo}
                          checkoutLoading={checkoutLoading}
                          isPre={isPre}
                          setShowOrderDetail={setShowOrderDetail}
                          showOrderDetail={showOrderDetail}
                          deleteCartItems={deleteCartItems}
                        />
                      </div>
                  }

                </div>
              </div>
            </div>
          </CheckoutLayout>

          <LearnMore
            showLearnMore={showLearnMore}
            setShowLearnMore={setShowLearnMore}
            learnMoreItem={learnMoreItem}
          />

        </ConfigProvider>
      </GoogleOAuthProvider>
    </>
  )
}

function LeftContent({ cartInfo, checkoutLoading, isPre, setShowOrderDetail, showOrderDetail, deleteCartItems }: any) {
  return (
    <>
      {
        cartInfo.notSupportSkus && cartInfo.notSupportSkus.length > 0
        &&
        <div className={styles['warning-tips']}>
          <WarningOutlined className={styles['icon-warning-out-lined']} />
          <span>
            {
              cartInfo.sendToPoBox
                ?
                `Sorry! Your selected item(s) cannot be shipped to a PO Box. Please change your shipping address above.`
                :
                `Sorry! We're unable to ship item(s) to your current address due to logistical issues. Pls try antoher address or cancel the highlighted item(s).`
            }
          </span>
        </div>
      }
      {
        cartInfo.items && cartInfo.items.length == 0
        &&
        <div className={styles['warning-tips']}>
          <WarningOutlined className={styles['icon-warning-out-lined']} />
          <span>
            {'Note: The products on the current page have been cleared. Please repurchase the products to continue your order.'}
          </span>
        </div>
      }
      <div className={styles['right-side']}>
        <h3 className={styles['summary-title']}>Order Summary</h3>

        {
          checkoutLoading
            ?
            <Skeleton active />
            :
            <>
              <div className={styles['price-line']}>
                <div className={styles['priceLabel']}>Sub Total:</div>
                <div className={styles['priceVal']}>{formatCurrency(cartInfo.subTotal)}</div>
              </div>
              <div className={styles['price-line']}>
                <div className={styles['priceLabel']}>Shipping:</div>
                <div className={styles['priceVal']}>{formatCurrency(cartInfo.shipping)}</div>
              </div>
              {
                !!cartInfo.discount
                &&
                <div className={styles['price-line']}>
                  <div className={styles['priceLabel']}>Coupon:</div>
                  <div className={styles['priceVal']}>-{formatCurrency(cartInfo.discount)}</div>
                </div>
              }
              {/* <div className={styles['price-line']}>
                          <div className={styles['priceLabel']}>Extra Discount:</div>
                          <div className={styles['priceVal']}>$10.88</div>
                        </div> */}
              {/* <div className={styles['price-line']}>
                          <div className={styles['priceLabel']}>Handling:</div>
                          <div className={styles['priceVal']}>$10.88</div>
                        </div> */}
              {/* <div className={styles['price-line']}>
                          <div className={styles['priceLabel']}>Delivery Choice Charge:</div>
                          <div className={styles['priceVal']}>$10.88</div>
                        </div> */}
              <div className={styles['price-line']}>
                <div className={styles['priceLabel']}>GST(Inc.):</div>
                <div className={styles['priceVal']}>{formatCurrency(cartInfo.gst)}</div>
              </div>

              <div className={styles['price-underline']}></div>

              <div className={styles['price-line']}>
                <div className={`${styles['priceLabel']} ${styles['bold']}`}>Total Amount (AUD):</div>
                <div className={`${styles['priceVal']} ${styles['bold']}`}>{formatCurrency(cartInfo.total)}</div>
              </div>

              <Button className={styles['back-viewcar']} href={isPre ? cartInfo.items[0].url : '/view-cart'} type="primary" icon={<LeftOutlined />} ghost >{isPre ? 'Change Quantity' : 'Back to Cart & Edit'}</Button>

              <div className={styles['show-order-details']} onClick={() => { setShowOrderDetail(!showOrderDetail) }}>
                Show Order Details &nbsp;&nbsp;
                {
                  showOrderDetail
                    ?
                    <UpOutlined />
                    :
                    <DownOutlined />
                }
              </div>

              <div className={styles['goods-table-list']} style={{ display: showOrderDetail ? 'block' : 'none', maxHeight: cartInfo.notSupportSkus && cartInfo.notSupportSkus.length > 0 ? '269px' : '400px' }}>
                <div className={`${styles['goods-table-tr']} ${styles['goods-table-head']}`}>
                  <div className={styles['goods-table-td']} style={{ width: "calc(25% + 40px)" }}>Image</div>
                  <div className={styles['goods-table-td']}>Quantity</div>
                  <div className={styles['goods-table-td']}>Unit Price</div>
                  <div className={styles['goods-table-td']}>Total</div>
                </div>
                {
                  !!(cartInfo.items && Object.keys(cartInfo.items).length)
                  &&
                  Object.entries(cartInfo.items).map(([key, item]: any) => (
                    <React.Fragment key={key}>
                      <div className={`${styles['goods-table-tr']} ${item.postage == -1 && styles['warning']}`}>
                        <div className={styles['goods-table-td']} style={{ width: "25px" }}>
                          {
                            item.postage == -1
                            &&
                            <Popconfirm
                              title="Delete Item"
                              description="Are you sure you want to delete the selected items?"
                              onConfirm={() => (
                                new Promise((resolve) => {
                                  deleteCartItems(item, resolve)
                                })
                              )}
                              okText="Yes"
                              cancelText="No"
                            >
                              <div className={styles['close-icon-box']}>
                                <CloseOutlined className={styles['close-icon']} />
                              </div>
                            </Popconfirm>
                          }
                        </div>
                        <div className={styles['goods-table-td']}>
                          <Image src={getCompressedImageUrl(item.defaultFullUrl, 112)} width={50} height={50} preview={false}></Image>
                        </div>
                        <div className={styles['goods-table-td']}>{item.quantity}</div>
                        <div className={styles['goods-table-td']}>{formatCurrency(item.price)}</div>
                        <div className={styles['goods-table-td']}>{formatCurrency(item.subTotal)}</div>
                      </div>
                      {item.isPresale && item.presaleDate ? (
                        <div className={styles["pre-end-date"]}>
                          Estimated shipping on&nbsp;{item.presaleDate}
                        </div>
                      ) : null}
                    </React.Fragment>
                  ))
                }

              </div>
            </>
        }
      </div>
    </>
  )
}
